(ns app.front.view.dispatch.core
  (:require ["@carbon/icons-react/es/task--complete/20" :default TaskComplete20]
            [app.domain.booking.model :as booking-model]
            [app.domain.inspection.model :as inspection-model]
            [app.domain.municipality.model :as municipality-model]
            [app.domain.util :as du]
            [app.domain.workspace.model :as workspace-model]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.chevron :as c]
            [app.front.ui.error-boundary :as eb]
            [app.front.ui.select :as ui-select]
            [app.front.ui.transitions :as t]
            [app.front.user :as user]
            [app.front.util :as futil :refer [for-frag for-frag-indexed]]
            [app.front.view.dispatch.dispatch-booking :as dispatch-booking]
            [app.front.view.dispatch.dispatch-map :as dispatch-map]
            [app.front.view.dispatch.email :as dispatch-email]
            [app.front.view.dispatch.subs :as dispatch-subs]
            [app.front.view.group.subs :as group-subs]
            [app.front.view.inspections.booking-utils :as butil]
            [app.front.view.inspections.summary-list :as inspection-summary]
            [app.front.view.workspace.get :as ws-get]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.href :as href]
            [core.front.icons :as i]
            [core.user.model :as user-model]
            [core.util.util :as util]
            [goog.functions]
            [re-frame.core-instrumented :as rf]
            [re-frame.db]
            [reagent.core :as r]
            [schema.core :as s]))


(defn booking-id-from-event [event]
  (futil/parse-int (.getData (.-dataTransfer event) "booking-id")))

(defn execution-id-from-event [event]
  (futil/parse-int (.getData (.-dataTransfer event) "execution-id")))

(defn accepting-booking-drop? [event]
  (contains? (-> event .-dataTransfer .-types set) "booking-id"))


(defn booking-toolbar-item [icon text on-click class]
  [:a.uppercase.w-full.flex.items-center.justify-between.px-3.py-2.text-xs.font-semibold.text-white.bg-gray-900.no-underline.hover:bg-gray-800
   {:href     "#"
    :on-click on-click
    :class    class}
   [:span text]
   (cc/icon {:class-name  "ml-2"
             :icon        icon
             :description ""
             :aria-hidden true
             :fill        "currentcolor"})])


(defn- booking-item-email-sent
  "Shows notification text if email has been sent"
  [tr mail-last-sent]
  (when mail-last-sent
    [:<>
     (cc/icon {:icon        i/envelope
               :fill        "#656464"
               :class-name  "h-4 mt-1"
               :description (tr [:dispatching/mail-last-sent])})
     [:span.h-4.m-1.leading-snug.absolute.text-gray-800
      {:title (tr [:dispatching/mail-last-sent])}
      ((<sub [::date-util/date-time-fn]) mail-last-sent)]]))


(defn- booking-item-details
  "The details that come into view when the booking is clicked on in the dispatch view"
  [{:keys [tr id workspace booking]}]
  (r/with-let [*options-expanded?           (r/atom false)
               *set-fulfilled-confirm-open? (r/atom false)]
    (let [visible?           (= id (<sub [::dispatch-subs/selected-booking-id]))
          dispatched?        (= (:domain/state booking) :booking.state/dispatched)
          ;; If this list of actions is longer than 2 components, an Options menu will be rendered and all but the last component
          ;; are rendered inside the Options menu
          actions            [(when dispatched?
                                [dispatch-email/send-mail-button tr id workspace booking])
                              [booking-toolbar-item
                               i/copy
                               (tr [:booking/copy])
                               #(>evt [::dispatch-subs/copy-booking (:db/id booking)])]
                              (when (<sub [:session/admin?])
                                [booking-toolbar-item
                                 i/file-check
                                 (tr [:booking/set-fulfilled])
                                 #(reset! *set-fulfilled-confirm-open? true)])
                              [booking-toolbar-item
                               i/comment
                               (tr [:dispatching/notes])
                               #(>evt [::dispatch-subs/open-notes-dialog booking])]]
          show-options-menu? (-> (count actions) (> 2))]
      [:div.mb-1.bg-gray-900.flex.flex-row.justify-end.relative.z-40.items-center
       (if (<sub [::dispatch-subs/dispatch-pending? (:db/id booking)])
         [:div.flex.justify-around.p-2.w-full
          (cc/loading-spinner {:small true :with-overlay false})]
         (if show-options-menu?
           [:<>
            [:div.relative.flex.self-stretch.w-1-2
             [:a.border-0.uppercase.w-full.flex.self-stretch.items-center.justify-between.px-3.py-2.text-xs.font-semibold.text-white.bg-gray-900.no-underline.hover:bg-gray-800
              {:href     "#"
               :on-click #(swap! *options-expanded? not)}
              (tr [:options])
              (cc/icon {:class-name  "ml-2"
                        :icon        i/chevron-small-down
                        :description ""
                        :aria-hidden true
                        :fill        "currentcolor"})]
             [t/transition
              {:transition      "fade-in--quick"
               :timeout         100
               :in              (and visible? @*options-expanded?)
               :unmount-on-exit false}
              [:div.bg-gray-900.absolute.flex.flex-col.w-48.overflow-hidden
               {:class    (if @*options-expanded? "h-auto" "h-0")
                :style    {:top "100%"}
                :on-click #(reset! *options-expanded? false)}
               (for-frag [component (butlast actions)]
                 component)]]]
            [:div.w-1-2 (last actions)]]
           (for-frag [component actions]
             component)))
       [ui/confirm-modal
        {:open?        @*set-fulfilled-confirm-open?
         :tr           tr
         :id           "set-booking-fulfilled-modal"
         :modal-title  (tr [:booking/set-fulfilled])
         :on-close     #(reset! *set-fulfilled-confirm-open? false)
         :on-yes-click #(>evt [::dispatch-subs/set-fulfilled (:db/id booking)])}
        (tr [:booking/set-fulfilled-confirm-text])]])))



(defn booking-qualifications-v2 [tr lang inspection inspector-qualifications details-visible?]
  (when-let [inspection-qualifications (:inspection/inspector-qualifications inspection)]
    [:div.overflow-hidden
     {:class (when-not details-visible? "h-0")
      :style {:transition "all 200ms ease-out"}}
     [:div.mt-2 (str (tr [:inspection.template/qualification]) ":")]
     (for-frag [qualification inspection-qualifications
                :let [show-icon? (some? inspector-qualifications)
                      met?       (or (not show-icon?)
                                     (contains? (du/entity-id-set inspector-qualifications) (:db/id qualification)))
                      title      (tr [(if met?
                                        :inspection/qualification-met
                                        :inspection/qualification-not-met)])]]
       [:div
        (when show-icon?
          (cc/icon {:icon        (if met? i/check i/remove)
                    :description title
                    :fill        (if met? "black" "gray")
                    :class-name  "w-3 h-3 mr-1"
                    :view-box    "-4 -4 20 20"}))
        [:span {:class-name (if met? "" "text-gray-700")
                :title      (when show-icon? title)}
         (inspection-model/inspector-qualification-name qualification lang)]])]))


(defn- booking-item-body
  "The main text of the booking item"
  [{:keys [tr booking workspace inspection inspector-qualifications show-times?]}]
  (let [details-visible?     (= (:db/id booking) (<sub [::dispatch-subs/selected-booking-id]))
        address              (or (:workspace/address workspace)
                                 (futil/location-coordinates workspace))
        lang                 (<sub [:language])
        municipality         (-> workspace
                                 :workspace/municipality
                                 (municipality-model/municipality-name lang))
        group-tz             (-> workspace :domain/authority-group :group/tz)
        date-requested-str   (some-> booking :entity/created (date-util/date-time-in-tz group-tz))
        show-requested-date? (and date-requested-str
                                  (<sub [::ff/enabled? :feature-flag.id/requested-date-on-bookings]))]
    [:div.flex-1.pr-1
     {:class (when (-> booking :domain/state (= :booking.state/cancelled))
               "line-through")}
     [:a.text-ui-blue.no-underline.font-bold.inline-block.mb-1.hover:underline
      {:draggable  false
       :target     "_blank"
       :on-click   #(.stopPropagation %)
       :href       (href/href :view/workspace {:workspace-id (:workspace/id workspace)})
       :aria-label (tr [:inspection.visit/link-to-application])}
      (->> [address municipality]
           (remove nil?)
           (str/join ", "))]
     [:div.mb-1 (workspace-model/case-id workspace)]
     [:div {:class (if show-requested-date? "mb-1" "")} (inspection-model/inspection-name inspection lang)]
     (when show-requested-date?
       [:div date-requested-str])
     (when (and show-times? (:booking/actual-start-time booking))
       [:div.mt-1 (str (date-util/hour-minute-in-tz (:booking/actual-start-time booking) group-tz)
                       " – "
                       (date-util/hour-minute-in-tz (:booking/actual-end-time booking) group-tz))])
     (when-let [mail-last-sent (:booking/mail-last-sent booking)]
       [:div [booking-item-email-sent tr mail-last-sent]])
     [booking-qualifications-v2 tr lang inspection inspector-qualifications details-visible?]]))


(defn has-notes? [{:booking/keys [notes cancel-reason dispatch-notes]}]
  (some seq [notes cancel-reason dispatch-notes]))


(defn external-event
  "Renders an external calendar event"
  [{:external-event/keys [subject start end]} slot-start-time slot-end-time group-tz]
  (let [continues-from-earlier-slot? (date-util/before? start slot-start-time)
        continues-to-later-slot?     (date-util/after? end slot-end-time)
        continues-both-ways?         (and continues-from-earlier-slot? continues-to-later-slot?)
        contained-in-slot?           (not (or continues-from-earlier-slot? continues-to-later-slot?))]
    [:div.pr-2.pl-3.bg-white.text-xs.select-none.relative
     {:class (futil/cs (when (and continues-from-earlier-slot? (not continues-both-ways?)) "sawtooth-top pb-2 pt-4 mb-2")
                       (when (and continues-to-later-slot? (not continues-both-ways?)) "sawtooth-bottom pb-4 pt-2")
                       (when continues-both-ways? "py-2")
                       (when contained-in-slot? "py-2 mb-2"))}
     [:div.mb-1.font-bold subject]
     [:div
      (str (date-util/hour-minute-in-tz start group-tz)
           " – "
           (date-util/hour-minute-in-tz end group-tz))]]))


(defn all-day-external-event
  "Renders an all-day external event"
  [{:external-event/keys [subject]}]
  [:div.px-3.py-2.mb-2.bg-ui-blue-100.text-xs.text-white.select-none.whitespace-nowrap.overflow-hidden.font-bold
   {:style {:text-overflow "ellipsis"}}
   subject])


(defn booking-item
  "The boxes the dispatcher drags and drops to dispatch it"
  [{:keys [booking inspector-qualifications show-times?]}]
  (r/with-let [is-dragging? (r/atom false)]
    (let [selected-date            (<sub [::dispatch-subs/date])
          id                       (:db/id booking)
          inspection               (:entity/parent booking)
          execution                (:entity/parent inspection)
          workspace                (:entity/parent execution)
          auth-group-id            (-> workspace :domain/authority-group :db/id)
          is-dispatched?           (not= :booking.state/to-dispatch (:domain/state booking))
          is-selected?             (= id (<sub [::dispatch-subs/selected-booking-id]))
          ;; difficult to check guards, because we would need to load the process state
          ;; machine of each execution
          can-dispatch?            (-> booking :domain/state booking-model/changeable-booking-states)
          draggable?               (and can-dispatch? (<sub [::dispatch-subs/no-dispatch-pending?]))
          tr                       (<sub [:tr])
          requested-date-selected? (date-util/same-day? selected-date (:booking/requested-start-time booking))]
      [:<>
       (when (and is-dispatched?
                  (= booking (<sub [::dispatch-subs/drag-over-booking])))
         ;; Draw a "drop target box" to indicate where the booking being dragged will
         ;; go when it is dragged over this booking.
         [:div.h-16.mb-1.bg-gray-300])

       [:div.mb-2.relative
        {:class        (futil/cs (when @is-dragging? "h-0 overflow-hidden")
                                 (when requested-date-selected? "border-l-4 border-solid border-blue-600"))
         :style        {:transition "border 100ms ease-out"}
         :on-drag-over (fn [e]
                         (.preventDefault e)
                         (when (accepting-booking-drop? e)
                           (>evt [::dispatch-subs/set-drag-over-booking (when is-dispatched?
                                                                          ;; non-dispatched bookings do not have order
                                                                          booking)])))}

        [:div.py-2.dispatch-booking-item.text-xs.select-none.relative.border-solid.border-2.hover:shadow-lg.relative.z-20
         {:draggable     draggable?
          :data-cy       "dispatch-booking-item"
          :class         [(if is-selected? "border-blue-600" "border-transparent")
                          (when requested-date-selected? "border-l-0")
                          (if draggable? "cursor-move bg-white" "bg-gray-200")]
          :on-click      (fn [_]
                           (>evt [::dispatch-subs/set-selected-booking id true])
                           (>evt [::dispatch-subs/deselect-inspector]))
          :on-drag-start (fn [e]
                           (reset! is-dragging? true)
                           (>evt [::dispatch-subs/set-dragged-booking-auth-group auth-group-id])
                           (>evt [::dispatch-subs/set-selected-booking id false])
                           (.setData (.-dataTransfer e) "booking-id" id)
                           (.setData (.-dataTransfer e) "execution-id" (:db/id execution)))
          :on-drag-end   (fn [_]
                           (reset! is-dragging? false)
                           (>evt [::dispatch-subs/set-drag-over-booking nil])
                           (>evt [::dispatch-subs/set-dragged-booking-auth-group nil]))}
         [:div.flex.px-2
          [booking-item-body (util/mmap tr booking workspace inspection inspector-qualifications show-times?)]
          [:div.flex.flex-col
           [:div.flex.flex-1
            (when (has-notes? booking)
              (cc/icon {:icon        i/comment
                        :fill        "#001627"
                        :class-name  "h-5"
                        :description (tr [:dispatching/request-has-notes])}))]
           [:div.flex.flex-1.items-end
            (when (du/in-state? :booking.state/fulfilled booking)
              [:span {:title (tr [:inspection/visit-end-email-title])}
               (r/create-element TaskComplete20 #js{:aria-label (tr [:inspection/visit-end-email-title])})])]]]
         (when-not (workspace-model/has-location workspace)
           [:div.px-2.pt-2 {:class "text-ui-red"} (tr [:dispatching/map-location-not-available])])]
        [t/transition
         {:transition "dispatch-slide-down"
          :timeout    200
          :in         is-selected?}
         [booking-item-details (util/mmap tr id workspace booking)]]]])))


(defn booking-drop-zone [{:keys [inspector-id time-slot group-tz all-days-bookings disabled?]} & children]
  (r/with-let [this        (r/current-component)
               *accepting? (r/atom false)]
    [:div
     (into (dissoc (r/props this) :inspector-id :time-slot :group-tz :all-days-bookings :disabled?)
           {:on-drop       (fn [e]
                             (reset! *accepting? false)
                             (let [booking-id   (booking-id-from-event e)
                                   execution-id (execution-id-from-event e)]
                               (when (and (not disabled?) (some? booking-id))
                                 (if (nil? inspector-id)
                                   (>evt [::dispatch-subs/cancel-dispatch
                                          booking-id
                                          execution-id])
                                   (>evt [::dispatch-subs/dispatch-booking
                                          inspector-id
                                          booking-id
                                          execution-id
                                          time-slot
                                          group-tz
                                          all-days-bookings])))))
            :on-drag-enter #(.preventDefault %)
            :on-drag-over  #(when-not disabled?
                              (.preventDefault %)
                              (when (accepting-booking-drop? %)
                                (reset! *accepting? true)))
            :on-drag-leave #(reset! *accepting? false)
            :class         (str "cloudpermit--booking-drop-zone pb-4 " (:class (r/props this)))})
     (vec (concat [:<>]
                  children
                  (when @*accepting?
                    [[:div.pb-16 {:class        "pb-16"
                                  :on-drag-over #(>evt [::dispatch-subs/set-drag-over-booking nil])}]])))]))


(defn render-request-editor [tr]
  [dispatch-booking/search tr])


(defn tab-button [{:keys [mode]} content]
  (let [selected-mode (<sub [::dispatch-subs/mode])
        selected?     (= mode selected-mode)]
    (cc/button
      {:size          :small
       :kind          (if selected? :ghost :primary)
       :on-click      #(>evt [::dispatch-subs/select-mode mode])
       :aria-selected selected?
       :role          "tab"
       :class-name    "flex-1 overflow-hidden text-xs p-1 uppercase"}
      (str content
           " ("
           (<sub [::dispatch-subs/item-count mode])
           ")"))))


(defn view-tabs []
  (let [tr (<sub [:tr])]
    [:div.flex.w-full.tab-control
     {:role "tablist"}
     [tab-button
      {:mode :requests}
      (tr [:dispatching/requests])]
     [tab-button
      {:mode :cancelled}
      (tr [:dispatching/cancelled])]]))


(defn booking-queue [bookings tr group-tz]
  (let [request-editor-open? (<sub [::dispatch-subs/request-editor-open?])]
    [:div.w-64.bg-gray-200.flex.flex-col.justify-stretch.flex-1.min-h-128
     [view-tabs]
     (if (<sub [::dispatch-subs/loading? :bookings-initial])
       [:div.flex.justify-around.py-32.w-full
        (cc/loading-spinner {:with-overlay false})]
       [:<>
        (when (not= (<sub [::dispatch-subs/mode]) :cancelled)
          (cc/button
            {:href             "#"
             :class-name       "w-full justify-start"
             :kind             :ghost
             :icon             (if request-editor-open? i/remove i/plus)
             :icon-description ""
             :on-click         #(>evt [::dispatch-subs/toggle-request-editor])}
            (if request-editor-open?
              (tr [:close])
              (tr [:dispatching/create-booking]))))
        (if request-editor-open?
          [render-request-editor tr]
          [booking-drop-zone
           {:inspector-id nil
            :time-slot    nil
            :data-cy      "booking-drop-zone-requests"
            :class        "overflow-y-scroll flex-auto"
            :style        {:height 0}}
           [:div.p-2
            [:div.w-full.px-4]
            (when (= 0 (count bookings))
              [:div.text-xl.text-gray-500.fade-in.w-full.px-4.py-2.flex.leading-normal.flex-col.justify-center
               (tr [(case (<sub [::dispatch-subs/mode])
                      :cancelled :dispatching/no-cancellations
                      :dispatching/no-requests-in-queue)])])
            (for-frag [[day time-slots-for-day] bookings]
              ^{:key day}
              [:div.mb-5.relative
               [:h1.date-header.mt-0.mb-3.px-2.overflow-hidden.font-normal.text-blue-700.text-sm.leading-snug.text-center
                day]
               (for-frag [[[start-time end-time] bookings-for-time-slot] time-slots-for-day
                          :let [key           (str day
                                                   (date-util/date-time-in-tz start-time group-tz)
                                                   (date-util/date-time-in-tz end-time group-tz))
                                time-opts     {:hour   "numeric"
                                               :minute "numeric"}
                                time-slot-str (str (date-util/in-tz-with-opts start-time group-tz time-opts)
                                                   " – "
                                                   (date-util/in-tz-with-opts end-time group-tz time-opts))]]
                 ^{:key key}
                 [:div.mb-5
                  [:div.mb-2.pl-2.text-xs.text-gray-800 time-slot-str]
                  (for-frag [{id :db/id :as booking} bookings-for-time-slot]
                    ^{:key (str "booking-item-" id)}
                    [booking-item (util/mmap booking)])])])]])])]))



(defn- render-time-slot
  [{:keys [group-tz bookings qualifications slot-start-time slot-end-time
           partial-day-external-events slot-label disabled? show-times?]}]
  [:<>
   [:div.text-xs.mb-2
    {:style (when disabled?
              {:color "#dddddd"})}
    slot-label]
   [:div.relative
    (for-frag [ext-evt partial-day-external-events]
      [external-event ext-evt slot-start-time slot-end-time group-tz])
    (for-frag [booking bookings
               :let [inspector-qualifications (-> qualifications
                                                  (get (-> booking
                                                           :entity/parent ;inspection
                                                           :entity/parent ;execution
                                                           :entity/parent ;workspace
                                                           :domain/authority-group
                                                           :db/id))
                                                  set)]]
      ^{:key (str "assigned-booking-item-" (:db/id booking))}
      [booking-item (util/mmap booking inspector-qualifications show-times?)])]])


(defn- droppable-time-slot
  [{:keys [id slot-id group-tz length slot slot-label bookings all-days-bookings disabled?
           qualifications slot-start-time slot-end-time partial-day-external-events]}]
  [booking-drop-zone
   {:key               (str "inspector-" id "-slot-" slot-id)
    :class             "p-2"
    :inspector-id      id
    :time-slot         slot
    :group-tz          group-tz
    :all-days-bookings all-days-bookings
    :disabled?         disabled?
    :style             (merge {:min-height    (str (* length 5) "em")
                               :border-bottom "2px solid #ddd"}
                              (when disabled?
                                {:background-color "#eeeeee"}))}
   [render-time-slot (util/mmap group-tz bookings qualifications slot-label disabled?
                                slot-start-time slot-end-time partial-day-external-events)]])


(defn- get-inspector-style [highlighted?]
  (if highlighted?
    (let [width "0.25rem"]
      {:border-style      "solid"
       :border-color      dispatch-map/highlight-color
       :borderTopWidth    width
       :borderBottomWidth width
       :borderLeftWidth   width
       :borderRightWidth  width})
    {:border-style "none"}))


(defn- get-qualification-ratio [authority-group-id qualifications available-qualifications parens?]
  (let [get-fn #(-> % (get authority-group-id) count)]
    (str (when parens? "(")
         (get-fn qualifications)
         "/"
         (get-fn available-qualifications)
         (when parens? ")"))))


(defn render-working-areas [label areas]
  (if (seq areas)
    [:div.mt-2.leading-tight
     [:div.text-gray-400.mb-1 label]
     (for-frag [area areas]
       [:div (:defined-area/label area)])]
    [:div]))

(defn- render-inspector-details [{:keys [tr qualifications available-qualifications
                                         combined-view? disabled? expanded? highlighted?
                                         primary-areas secondary-areas]}]
  (let [lang (<sub [:language])]
    [:div.bg-gray-900.text-white.overflow-hidden.absolute.top-0.w-full.z-50
     {:class (when disabled? "bg-gray-300")
      :style (-> (get-inspector-style highlighted?)
                 (dissoc :borderTopWidth))}
     [:div.border-solid.border-gray-700
      {:class (concat (if expanded?
                        ["h-auto" "pb-2" "border-t"]
                        ["h-0" "py-0"])
                      (if highlighted?
                        ["px-2"]
                        ["px-3"])
                      (when disabled? ["bg-gray-300"]))}
      [:div.text-gray-400.leading-tight
       (if combined-view?
         (tr [:inspection/qualifications])
         [:div.pt-2 (tr [:inspection/qualifications]) " "
          (get-qualification-ratio (ffirst available-qualifications)
                                   qualifications
                                   available-qualifications
                                   true)])]
      (for-frag [[authority-group-id authority-group-qualifications] qualifications
                 :let [ratio (get-qualification-ratio authority-group-id
                                                      qualifications
                                                      available-qualifications
                                                      true)
                       title (str (:group/name (<sub [::entities/entity authority-group-id])) " " ratio)]]
        [:div.mb-1.mt-1.leading-tight
         (when combined-view?
           [:div.text-gray-400.mb-1 title])
         (for-frag [qualification (->> authority-group-qualifications
                                       (map #(inspection-model/inspector-qualification-name % lang))
                                       sort)]
           [:div qualification])])
      [render-working-areas (tr [:inspection.areas/primary-areas]) primary-areas]
      [render-working-areas (tr [:inspection.areas/secondary-areas]) secondary-areas]]]))

(defn overlap-fn [date group-tz]
  (fn [{:external-event/keys [start end]}]
    (date-util/overlap? start
                        end
                        (-> date
                            date-util/start-of-day
                            (date-util/in-tz group-tz))
                        (-> date
                            date-util/end-of-day
                            (date-util/in-tz group-tz)))))

(defn- inspector-disabled? [inspector]
  (let [booking-id (<sub [::dispatch-subs/selected-booking-id])
        inspection (->> booking-id
                        (#(<sub [::entities/entity %]))
                        :entity/parent)
        execution  (:entity/parent inspection)]
    (and (not (inspection-model/no-inspection-qualification-restriction?
                (util/mmap {:user   inspector
                            :target execution}
                           inspection
                           booking-id)))
         ;; Restriction can be bypassed by setting the `assign-to-unqualified`.
         (not (<sub [::dispatch-subs/assign-to-unqualified?])))))

(defn- render-inspector [{:keys [index date inspector group-tz tr]}]
  (r/with-let [*details-visible? (r/atom false)]
    (let [id                        (:db/id inspector)
          selected-inspector-id     (<sub [::dispatch-subs/selected-inspector-id])
          selected?                 (= id selected-inspector-id)
          name                      (user-model/full-name inspector)
          user-bookings             (<sub [::dispatch-subs/user-bookings-for-date id])
          time-slots+bookings       (<sub [::dispatch-subs/user-time-slots+bookings-for-date id])
          active-auth-group-ids     (<sub [::dispatch-subs/active-authority-group-ids])
          qualifications            (inspection-model/get-inspector-qualifications inspector active-auth-group-ids)
          disabled?                 (inspector-disabled? inspector)
          available-qualifications  (reduce (fn [acc id]
                                              (assoc acc id (<sub [::group-subs/available-qualifications id])))
                                            {}
                                            active-auth-group-ids)
          combined-view?            (< 1 (count active-auth-group-ids))
          qualifications-ratio      (when-not combined-view?
                                      (get-qualification-ratio (first active-auth-group-ids)
                                                               qualifications
                                                               available-qualifications
                                                               false))
          {:keys [primary-areas
                  secondary-areas]} inspector
          max-all-day-event-count   (<sub [::dispatch-subs/max-all-day-event-count date])
          external-events           (<sub [::dispatch-subs/external-events-for-user id])
          all-day-external-events   (->> external-events
                                         (filter (every-pred dispatch-subs/all-day-event?
                                                             (overlap-fn date group-tz)))
                                         (sort-by :external-event/start))
          all-day-section-height    (if (< 0 max-all-day-event-count)
                                      (str (+ 20 (* max-all-day-event-count 40)) "px")
                                      "0px")]
      [:div.mr-2.select-none.top-0.dispatch-available-inspector.bg-gray-100.flex.flex-col
       {:key   (str "inspector-" id)
        :style {:min-width        "267px"
                :height           "calc(100vh - 320px)"
                :left             (str (* index 278) "px")
                :transition-delay (str (* index 30) "ms")}}
       [:div.h-12.text-white
        {:style (-> (get-inspector-style selected?)
                    (assoc :borderBottomWidth 0))
         :class (futil/cs (if disabled?
                            "bg-gray-300"
                            "bg-gray-900")
                          (if selected?
                            "px-1"
                            "px-2"))}
        [:div.inline-block.cursor-pointer
         {:on-click #(swap! *details-visible? not)}
         [c/chevron {:icon      i/chevron-right
                     :side      :left
                     :size      "1rem"
                     :class     (futil/cs "mx-3 mb-3 cursor-pointer inline "
                                          (if selected? "mt-3" "mt-4"))
                     :expanded? @*details-visible?
                     :tr        tr}]]

        [:div.h-full.inline-block.mx-2.border-l.border-solid.border-gray-700] ;Separator
        [:div.w-3-4.inline-block.align-top.cursor-pointer
         {:class    (if selected?
                      "py-2"
                      "pt-3 pb-2")
          :on-click #(if selected?
                       (>evt [::dispatch-subs/deselect-inspector])
                       (>evt [::dispatch-subs/select-inspector-id (:db/id inspector)]))}
         [:div.block.w-48 name]
         (when (some? qualifications-ratio)
           [:div.block (str "Q " qualifications-ratio)])]]

       [:div.relative
        {:style {:height (str "calc(100% - 48px - " all-day-section-height ")")}}
        [render-inspector-details (util/mmap {:expanded?    @*details-visible?
                                              :highlighted? selected?}
                                             tr disabled? qualifications available-qualifications combined-view?
                                             primary-areas secondary-areas)]
        (when (< 0 max-all-day-event-count)
          [:div.px-2.pt-2
           {:style {:min-height all-day-section-height}}
           [:div.mb-2.text-xs (tr [:dispatching/all-day-events])]
           (for-frag [ext-evt all-day-external-events]
             [all-day-external-event ext-evt])])

        [:div.flex-1.overflow-y-scroll.overflow-x-hidden.h-full
         (for-frag [[slot slot-bookings] time-slots+bookings
                    :when slot
                    :let [{:time-slot/keys [start-hour start-minute end-hour end-minute]} slot
                          slot-start-time             (date-util/date-at-time-in-tz date start-hour start-minute group-tz)
                          slot-end-time               (date-util/date-at-time-in-tz date end-hour end-minute group-tz)
                          length                      (- end-hour start-hour)
                          slot-id                     (:db/id slot)
                          slot-label                  (str (date-util/hour-minute-in-tz slot-start-time group-tz)
                                                           " – "
                                                           (date-util/hour-minute-in-tz slot-end-time group-tz))
                          slot-external-events        (->> external-events
                                                           (filter (fn [{:external-event/keys [start end]}]
                                                                     (and start
                                                                          end
                                                                          (date-util/overlap? slot-start-time slot-end-time start end)
                                                                          (not= start slot-end-time))))
                                                           (sort-by (juxt :external-event/start :external-event/end))
                                                           set)
                          partial-day-external-events (set/difference slot-external-events all-day-external-events)]]
           [droppable-time-slot (util/mmap {:bookings slot-bookings :all-day-bookings user-bookings :external-events slot-external-events}
                                           id slot-id disabled? group-tz length slot slot-label
                                           slot-start-time slot-end-time qualifications
                                           all-day-external-events partial-day-external-events)])
         (when-let [overflow-bookings (<sub [::dispatch-subs/user-overflow-bookings-for-date id])]
           [:<>
            [:div.p-2
             [render-time-slot (util/mmap {:bookings    overflow-bookings
                                           :slot-label  (tr [:dispatching/overflow-time-slot-title])
                                           :show-times? true}
                                          group-tz qualifications)]]])]]])))


(defn notes-dialog [tr]
  (let [open?         (<sub [::dispatch-subs/notes-dialog-open?])
        booking-id    (<sub [::dispatch-subs/selected-booking-id])
        booking       (<sub [::entities/entity booking-id])
        lang          (<sub [:language])
        inspection    (:entity/parent booking)
        type          (inspection-model/inspection-name inspection lang)
        execution     (:entity/parent inspection)
        execution-id  (:db/id execution)
        workspace     (:entity/parent execution)
        booking-notes (:booking/notes booking)
        cancel-reason (:booking/cancel-reason booking)
        value         (<sub [::dispatch-subs/dispatch-notes-draft])
        address       (:workspace/address workspace)
        creator       (:entity/creator booking)
        creator-name  (str (:user/first-name creator) " " (:user/last-name creator))]
    (cc/modal
      {:open             open?
       :passive-modal    true
       :modal-heading    (tr [:dispatching/note-dialog-title])
       :on-request-close #(>evt [::dispatch-subs/toggle-notes-dialog false])}
      [:div
       [:div.mb-4
        [:h4.mb-4 (tr [:dispatching/previous-inspections])]
        (if (<sub [::ws-get/fetching-workspace? (:workspace/id workspace)])
          [cc/loading-spinner {:with-overlay false}]
          [inspection-summary/summary-list {:workspace-id (:workspace/id workspace)
                                            :booking      booking
                                            :tr           tr
                                            :class        "mb-4"}])]
       [ui/text-area
        {:id          "attachment-acceptance-text"
         :label-text  (tr [:dispatching/dispatcher-notes])
         :disabled    (-> booking :domain/state booking-model/changeable-booking-states not)
         :value       value
         :on-change   #(rf/dispatch-sync [::dispatch-subs/update-dispatch-notes-draft
                                          execution-id
                                          booking-id
                                          (-> % .-target .-value)])
         :on-blur     #(rf/dispatch-sync [::dispatch-subs/save-dispatch-notes-now
                                          execution-id
                                          booking-id])
         :placeholder (tr [:dispatching/dispatcher-note-placeholder])}] ;; TODO check why this is not working
       (when cancel-reason
         [:<>
          [:div.text-xs.mt-4.mb-2 (tr [:booking/cancel-reason])]
          [:pre.p-4.border-solid.border-gray-200.border-2.flex-1 cancel-reason]])
       [:div.flex.flex-row
        [:div.flex-1.flex.flex-col
         [:div.text-xs.mt-4.mb-2 (tr [:dispatching/request-details])]
         [:div.flex-1.p-4.border-solid.border-gray-200.border-2
          [:div.font-semibold address]
          [:div.font-semibold.mb-1 creator-name]
          [:div.text-gray-800 type]]]
        [:div.flex-1.ml-8.flex.flex-col
         [:div.text-xs.mt-4.mb-2 (tr [:dispatching/request-notes])]
         (if (str/blank? booking-notes)
           [:span.text-gray-500 (tr [:dispatching/no-request-notes])]
           [:pre.p-4.border-solid.border-gray-200.border-2.flex-1 booking-notes])]]])))


(defn header [{:keys [tr date group-tz]}]
  (let [select-authority-group-items  (<sub [::dispatch-subs/select-authority-group-items])
        selected-authority-group-item (<sub [::dispatch-subs/selected-authority-group-item])
        group-time-slots              (<sub [::dispatch-subs/group-time-slots])
        group-blackouts               (<sub [::dispatch-subs/group-blackouts])
        display-new-group-select?     (du/at-least-two? select-authority-group-items)
        previous-date                 (date-util/add-days-to-date date -1)
        next-date                     (date-util/add-days-to-date date 1)
        previous-date-identical?      (<sub [::dispatch-subs/time-slots-identical-for-date? previous-date])
        next-date-identical?          (<sub [::dispatch-subs/time-slots-identical-for-date? next-date])
        ctx                           (<sub [::entities/context])
        groups                        (<sub [::dispatch-subs/dispatcher-authority-groups])]
    [:div.flex.flex-col.lg:flex-row.justify-between.ml-4.gap-4
     [:div.flex.flex-col.md:flex-row.items-start.md:items-center
      {:style {:max-width "550px"}}
      [:div.flex.flex-col
       (if-not display-new-group-select?
         [:h3 (:label selected-authority-group-item)]
         [ui-select/select {:id           "authority-group-select"
                            :class-name   "unset-width-select"
                            :label-text   (tr [:dispatching/group-filter])
                            :allow-empty? false
                            :value        (if (<sub [::dispatch-subs/use-unified-view?])
                                            :all
                                            selected-authority-group-item)
                            :values       select-authority-group-items
                            :val->str     :label
                            :on-change    (fn [{:keys [value]}]
                                            (>evt (if (= value :all)
                                                    [::dispatch-subs/set-use-unified-view true]
                                                    [::dispatch-subs/select-authority-group value])))}])
       (when (and display-new-group-select?
                  (not (<sub [::dispatch-subs/time-slots-identical?])))
         [:div.mt-2.text-xs.hidden.lg:block
          (tr [:dispatching/time-slot-unify-info] [group-tz])])]]
     [:div.flex.flex-col.items-start.lg:items-end
      {:class (when display-new-group-select? "lg:pt-6")}
      [:div.flex.flex-row.items-center
       [:span.font-bold.mr-2.hidden.xl:block
        (futil/tr-weekday tr (.getDay date))
        (when (date-util/today? date) (str " (" (tr [:date/today]) ")"))]
       [:a.px-2
        {:href     "#"
         :on-click #(>evt [::dispatch-subs/select-date previous-date previous-date-identical?])}
        (cc/icon {:icon        i/chevron-left
                  :description (tr [:dispatching/previous-date])
                  :fill        "var(--text-dark)"})]
       [ui/date-picker {:tr          tr
                        :value       date
                        :allow-date? (butil/allow-date-fn group-tz
                                                          group-time-slots
                                                          group-blackouts)
                        :callback    (fn [d] (when (not (nil? d))
                                               (let [identical? (dispatch-subs/time-slots-identical? {:date   d
                                                                                                      :ctx    ctx
                                                                                                      :groups groups})]
                                                 (>evt [::dispatch-subs/select-date d identical?]))))}]
       [:a.px-2
        {:href     "#"
         :on-click #(>evt [::dispatch-subs/select-date next-date next-date-identical?])}
        (cc/icon {:icon        i/chevron-right
                  :description (tr [:dispatching/next-date])
                  :fill        "var(--text-dark)"})]]
      [ui/time-zone-info {:group-tz             group-tz
                          :tz-format            "longGeneric"
                          :tr-key               :inspection/time-zone-info
                          :hide-when-identical? true
                          :class                "mt-2 text-xs"}]]]))


(defn- inspector-sort-options [tr]
  (let [sort-options    (<sub [::dispatch-subs/available-inspector-sort-options])
        enabled-options (<sub [::dispatch-subs/enabled-inspector-sort-options])]
    [:div.flex.flex-row.items-center.gap-2
     [:div.shrink (str (tr [:dispatching/inspector-sort-title]) ":")]
     (for-frag [sort-option sort-options]
       [:div.shrink
        (cc/checkbox {:id         sort-option
                      :label-text (tr [sort-option])
                      :checked    (contains? enabled-options sort-option)
                      :on-change  #(>evt [::dispatch-subs/set-inspector-sort-option sort-option %])})])]))


(defn- assign-to-unqualified [tr]
  [:div.flex.flex-row
   (cc/checkbox {:id         "assign-to-unqualified"
                 :label-text (tr [:dispatching/assign-to-unqualified])
                 :checked    (<sub [::dispatch-subs/assign-to-unqualified?])
                 :on-change  #(>evt [::dispatch-subs/set-assign-to-unqualified %])})])


(defn main-content [{:keys [tr group-tz date]}]
  (let [inspectors           (<sub [::dispatch-subs/visible-inspectors])
        queue-bookings       (<sub [::dispatch-subs/bookings-for-dispatch])
        bookings-for-date    (<sub [::dispatch-subs/bookings-for-date])
        selected-booking-id  (<sub [::dispatch-subs/selected-booking-id])
        map-visible?         (<sub [::dispatch-subs/map-visible?])
        group-gis-id         (<sub [::dispatch-subs/selected-group-gis-customer-id])
        map-municipality-ids (or (->> (<sub [::entities/entities {:entity/type :entity.type/booking}])
                                      (keep (comp workspace-model/customer-id
                                                  booking-model/get-workspace))
                                      distinct
                                      seq)
                                 [group-gis-id])]
    [:section
     (when (seq map-municipality-ids)
       [:<>
        [:div.flex.flex-row.justify-end
         (cc/button
           {:kind     :ghost
            :size     :small
            :on-click #(>evt [::user/toggle-config-flag :user.config-flag/dispatch-map-hidden?])}
           (tr [(if map-visible?
                  :dispatching/hide-map
                  :dispatching/show-map)]))]
        (when map-visible?
          [:div.flex.flex-row
           [:div.w-full {:style {"height" "500px"}}
            [dispatch-map/dispatch-map map-municipality-ids bookings-for-date selected-booking-id]]])])

     [:div.flex.flex-col.mt-6.gap-4
      [assign-to-unqualified tr]
      [inspector-sort-options tr]]

     [:div.flex.flex-row
      [:div.flex.flex-col
       [:div.flex.flex-row.py-4.justify-between.items-center.h-12
        [:div.font-bold (tr [:dispatching/requests-queue])]]
       [booking-queue queue-bookings tr group-tz]]

      [:div.flex.flex-col.overflow-hidden.flex-1
       [:div.flex.flex-row.px-2.justify-between.items-center.h-12
        [:div.font-bold (tr [:dispatching/available-inspectors])]]

       [:div.flex.flex-row.overflow-hidden.flex-1
        ;; Available inspectors
        [:div.inspectors.flex.flex-row.overflow-x-scroll.flex-1.items-stretch.relative.overflow-y-hidden.ml-2
         (if (<sub [::dispatch-subs/loading-any?])
           [:div.flex.justify-around.py-6.w-full
            (cc/loading-spinner {:with-overlay false})]
           (for-frag-indexed [[index inspector] inspectors]
             [eb/error-boundary
              [render-inspector (util/mmap index date inspector group-tz tr)]]))]]]]]))


(defn view [_]
  (let [tr       (<sub [:tr])
        date     (<sub [::dispatch-subs/date])
        group-tz (<sub [::dispatch-subs/group-tz])]
    [:div.cloudpermit-page
     [header (util/mmap tr date group-tz)]
     [main-content (util/mmap tr date group-tz)]
     [notes-dialog tr]]))

(def route
  {:name        :view/dispatch
   :breadcrumb  {:items [:view/dispatch]}
   :view        view
   :public?     false
   :parameters  {:query {(s/optional-key :group-id) s/Int}}
   :controllers [{:parameters {:query [:group-id]}
                  :start      (fn [{{:keys [group-id]} :query}]
                                (>evt [::dispatch-subs/init group-id]))}]})
